import React, { useContext, useEffect, useState } from "react";
import { Title } from "../../../../../stories/Title/Title";
import { Button } from "../../../../../stories/Button/Button";
import { useNavigate } from "react-router-dom";
import {
  LicenseTypes,
  Operation,
  PageFor,
} from "../../../../../utils/Enum.types";
import Add from "./Add";
import List from "./List";
import { IObjectKeys } from "../../../../../utils/formTypes";
import { Form, Formik, FormikProps } from "formik";
import { categoryValidation } from "../../../../../utils/validationSchema";
import { AddDeviceType, UpdateDeviceType } from "./queries/mutations";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  AddDeviceTypeResult,
  AddDeviceTypeVar,
  AtsDeviceTypes,
  UpdateDeviceTypeResult,
  UpdateDeviceTypeVar,
} from "./queries/types";
import { emptyMessage } from "../../../../../utils/constants";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import { GetDeviceTypes, GetDeviceTypesNodeById } from "./queries/queries";
import { nodeVar } from "../../../../../utils/commonQueryTypes";
import { AppContext } from "../../../../../context/context";
import MessageModal from "../../../../../customComponents/MessageModal";
import { msgType } from "../../../../../utils/types";
import { MasterDataProps } from "../itemgroup/Index";
import Close from "../../../../../images/Close.svg";
import LoadingModal from "../../../../pages/LoadingModal";
import Navbar from "../../../../common/layout/Navbar";

interface formDataType extends IObjectKeys {
  groupName: string;
}

export interface CommonProps extends MasterDataProps {
  setOperation: React.Dispatch<React.SetStateAction<Operation>>;
  handleClear?: any;
}
export interface AddMasterDataProps extends CommonProps {
  formData: formDataType;
  handleValueChange: (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    meta: FormikProps<formDataType>
  ) => void;
  meta: FormikProps<formDataType>;
  operation: Operation;
}

export interface ListPageProps extends CommonProps {
  setMessage: React.Dispatch<React.SetStateAction<msgType>>;
}

const DeviceType = ({ type, setModal }: MasterDataProps) => {
  const navigate = useNavigate();
  const [message, setMessage] = useState(emptyMessage);
  const { user_details } = useLoggedInUserDetails();
  const { state } = useContext(AppContext);
  const [operation, setOperation] = useState(Operation.CREATE);
  const { customer_id } = useUrlParams();
  const [formData, setFormData] = useState<formDataType>({
    groupName: "",
  });

  const [getDeviceTypesByNodeId, { loading: GetNodeLoading }] = useLazyQuery<
    { node: AtsDeviceTypes },
    nodeVar
  >(GetDeviceTypesNodeById);

  const [addDeviceType, { loading: addLoading }] = useMutation<
    AddDeviceTypeResult,
    AddDeviceTypeVar
  >(AddDeviceType, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });
  const [updateDeviceType, { loading: updateLoading }] = useMutation<
    UpdateDeviceTypeResult,
    UpdateDeviceTypeVar
  >(UpdateDeviceType, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const handleValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    meta: FormikProps<formDataType>
  ) => {
    const { name, value } = e.target as HTMLInputElement;

    setFormData(() => ({
      ...formData,
      [name]: value,
    }));
    meta.handleChange(e);
  };
  const handleClear = () => {
    const inputElement = document.getElementsByName(
      "groupName"
    )[0] as HTMLInputElement;
    if (inputElement) inputElement.focus();
    setFormData({
      groupName: "",
    });
  };

  const handleSubmit = async () => {
    if (customer_id) {
      if (operation === Operation.CREATE) {
        const empAdded = addDeviceType({
          variables: {
            user_details,
            customer_id,
            deviceType: formData.groupName,
          },
          refetchQueries: [
            {
              query: GetDeviceTypes,
              variables: {
                customerID: customer_id,
              },
            },
          ],
        });
        const { data } = await empAdded;

        if (data && data.AddDeviceType) {
          setMessage({
            flag: true,
            message: "Device Type added successfully",
            operation: Operation.CREATE,
          });
        }
      } else {
        const customerUpdated = updateDeviceType({
          variables: {
            user_details,
            deviceTypeID: state.deviceTypeID,
            deviceType: formData.groupName,
          },
          refetchQueries: [
            {
              query: GetDeviceTypes,
              variables: {
                customerID: customer_id,
              },
            },
          ],
        });
        const { data } = await customerUpdated;

        if (data && data.UpdateDeviceType) {
          setMessage({
            flag: true,
            message: "Device Type details updated successfully",
            operation: Operation.CREATE,
          });
        }
      }
    }
  };

  const hanldeClear = () => {
    setFormData({
      groupName: "",
    });
  };
  const handleClose = () => {
    if (message.operation !== Operation.NONE) {
      hanldeClear();
      setOperation(Operation.CREATE);
    }
    setMessage(emptyMessage);
  };

  useEffect(() => {
    if (state.deviceTypeID && operation === Operation.UPDATE) {
      getDeviceTypesByNodeId({
        variables: {
          id: state.deviceTypeID,
        },
      }).then(({ data }) => {
        if (data && data.node) {
          setFormData({
            groupName: data.node.deviceType,
          });
        }
      });
    }
  }, [state.deviceTypeID, getDeviceTypesByNodeId, operation]);

  return (
    <>
      {type === PageFor.GENERAL && (
        <Navbar NavType={LicenseTypes.CUSTOMER_USERS} />
      )}
      <div className="master-data--modal--title">
        <Title>Device Type</Title>
        {type === PageFor.MODAL && (
          <img src={Close} alt="" onClick={() => setModal?.(false)} />
        )}
      </div>

      <Formik
        initialValues={formData}
        validationSchema={categoryValidation}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnChange
      >
        {(meta) => {
          return (
            <Form
              className={`master-data${
                type === PageFor.MODAL ? "--modal" : ""
              }`}
            >
              <div
                className={`master-data__datablock${
                  type === PageFor.MODAL ? "--modal" : ""
                }`}
              >
                <div className="master-data__datablock--left">
                  <Add
                    type={PageFor.GENERAL}
                    formData={formData}
                    handleValueChange={handleValueChange}
                    setOperation={setOperation}
                    meta={meta}
                    operation={operation}
                  />
                </div>
                <div className="master-data__datablock--right">
                  <List
                    type={PageFor.GENERAL}
                    setOperation={setOperation}
                    setMessage={setMessage}
                  />
                </div>
              </div>
              <Button mode="save" className="submit" type="submit" />
              <Button mode="clear" type="button" onClick={handleClear} />
              {type === PageFor.GENERAL ? (
                <Button
                  mode="back"
                  type="button"
                  onClick={() => navigate(-1)}
                />
              ) : (
                <Button
                  mode="cancel"
                  type="button"
                  onClick={() => setModal?.(false)}
                />
              )}
            </Form>
          );
        }}
      </Formik>
      <MessageModal
        handleClose={handleClose}
        modalFlag={message.flag}
        operation={message.operation}
        value={message.message}
      />
      <LoadingModal flag={GetNodeLoading || addLoading || updateLoading} />
    </>
  );
};

export default DeviceType;
